import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Book, Award, HelpCircle, GraduationCap, ChevronRight } from 'lucide-react';

const Learn = () => {
  useEffect(() => {
    document.title = 'Learn - FemSportNet';
  }, []);

  const features = [
    {
      icon: <Book className="h-8 w-8 text-green-500" />,
      title: 'Platform Features',
      description: 'Discover all the tools and features available to help you succeed in your sports journey.',
      link: '#features'
    },
    {
      icon: <Award className="h-8 w-8 text-green-500" />,
      title: 'Success Stories',
      description: 'Read inspiring stories from athletes who have achieved their goals using FemSportNet.',
      link: '#success-stories'
    },
    {
      icon: <HelpCircle className="h-8 w-8 text-green-500" />,
      title: 'FAQs',
      description: 'Find answers to common questions about using the platform and getting started.',
      link: '#faqs'
    },
    {
      icon: <GraduationCap className="h-8 w-8 text-green-500" />,
      title: 'Resources',
      description: 'Access educational content and guides for sports development and career growth.',
      link: '#resources'
    }
  ];

  const faqs = [
    {
      question: 'How do I get started on FemSportNet?',
      answer: 'Create an account, complete your profile with your sports background and achievements, and start connecting with other athletes and teams.'
    },
    {
      question: 'Is FemSportNet free to use?',
      answer: 'Yes, basic features are free. We also offer premium plans with additional features for serious athletes and teams.'
    },
    {
      question: 'How can I verify my achievements?',
      answer: 'Submit official documentation or certificates through our verification system. Our team will review and verify your achievements within 48 hours.'
    },
    {
      question: 'Can I find sponsorship opportunities?',
      answer: 'Yes, we regularly feature sponsorship opportunities from brands looking to support female athletes.'
    }
  ];

  return (
    <div className="bg-white dark:bg-gray-900 min-h-screen transition-colors duration-200">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-green-800 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Learn About FemSportNet
            </h1>
            <p className="text-xl text-green-100 mb-8">
              Your comprehensive guide to making the most of our platform and advancing your sports career.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center px-6 py-3 bg-white text-green-700 font-medium rounded-md hover:bg-green-50 transition-colors duration-200"
            >
              Get Started
              <ChevronRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800 transition-colors duration-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <a
                key={index}
                href={feature.link}
                className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-200 transform hover:-translate-y-1"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {feature.description}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section id="faqs" className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto">
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow p-6"
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section id="resources" className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-12">
            Educational Resources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-700 rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Training Guides
              </h3>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-600 dark:text-gray-300">
                  <ChevronRight className="h-4 w-4 mr-2 text-green-500" />
                  Strength and Conditioning
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-300">
                  <ChevronRight className="h-4 w-4 mr-2 text-green-500" />
                  Nutrition for Athletes
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-300">
                  <ChevronRight className="h-4 w-4 mr-2 text-green-500" />
                  Recovery Techniques
                </li>
              </ul>
            </div>

            <div className="bg-white dark:bg-gray-700 rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Career Development
              </h3>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-600 dark:text-gray-300">
                  <ChevronRight className="h-4 w-4 mr-2 text-green-500" />
                  Personal Branding
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-300">
                  <ChevronRight className="h-4 w-4 mr-2 text-green-500" />
                  Sponsorship Guide
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-300">
                  <ChevronRight className="h-4 w-4 mr-2 text-green-500" />
                  Media Training
                </li>
              </ul>
            </div>

            <div className="bg-white dark:bg-gray-700 rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Mental Health
              </h3>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-600 dark:text-gray-300">
                  <ChevronRight className="h-4 w-4 mr-2 text-green-500" />
                  Performance Psychology
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-300">
                  <ChevronRight className="h-4 w-4 mr-2 text-green-500" />
                  Stress Management
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-300">
                  <ChevronRight className="h-4 w-4 mr-2 text-green-500" />
                  Work-Life Balance
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-green-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Join thousands of female athletes who are already benefiting from our platform.
          </p>
          <div className="flex justify-center space-x-4">
            <Link
              to="/register"
              className="px-6 py-3 bg-white text-green-700 font-medium rounded-md hover:bg-green-50 transition-colors duration-200"
            >
              Sign Up Now
            </Link>
            <Link
              to="/contact"
              className="px-6 py-3 bg-transparent border-2 border-white text-white font-medium rounded-md hover:bg-white hover:text-green-700 transition-colors duration-200"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Learn;